/// OBS error-monitoring API — `/api/v1/diagnostics/*`.
/// Reference: API Contracts §1–§2, Frontend DD §5.
import { apiClient } from '@/core/api/axios-client'
import type {
  ClientErrorReport,
  CursorPaginated,
  ErrorGroupDetail,
  ErrorGroupSummary,
  ErrorOccurrenceDetail,
  ErrorOccurrenceSummary,
  ErrorStatus,
} from '@/types/api'

export interface ListErrorsQuery {
  layer?: string
  severity?: string
  status?: string
  platform?: string
  appVersion?: string
  tenantId?: string
  from?: string
  to?: string
  q?: string
  sort?: 'lastSeen' | 'count' | 'firstSeen'
  cursor?: string
  limit?: number
}

export const errorMonitoringApi = {
  async list(query: ListErrorsQuery = {}): Promise<CursorPaginated<ErrorGroupSummary>> {
    const res = await apiClient.get<CursorPaginated<ErrorGroupSummary>>('/diagnostics/errors', {
      params: query,
    })
    return res.data
  },

  async getDetail(fingerprint: string): Promise<ErrorGroupDetail> {
    const res = await apiClient.get<ErrorGroupDetail>(`/diagnostics/errors/${fingerprint}`)
    return res.data
  },

  async listOccurrences(
    fingerprint: string,
    query: { cursor?: string; limit?: number } = {},
  ): Promise<CursorPaginated<ErrorOccurrenceSummary>> {
    const res = await apiClient.get<CursorPaginated<ErrorOccurrenceSummary>>(
      `/diagnostics/errors/${fingerprint}/occurrences`,
      { params: query },
    )
    return res.data
  },

  async getOccurrence(fingerprint: string, eventId: string): Promise<ErrorOccurrenceDetail> {
    const res = await apiClient.get<ErrorOccurrenceDetail>(
      `/diagnostics/errors/${fingerprint}/occurrences/${eventId}`,
    )
    return res.data
  },

  async updateStatus(fingerprint: string, status: ErrorStatus): Promise<ErrorGroupSummary> {
    const res = await apiClient.patch<ErrorGroupSummary>(
      `/diagnostics/errors/${fingerprint}/status`,
      { status },
    )
    return res.data
  },

  /** PUBLIC client error report — best-effort, fire-and-forget (không throw lên UI). */
  async reportClientError(report: ClientErrorReport): Promise<void> {
    await apiClient.post('/diagnostics/client-errors', report)
  },
}
